// CampaignEditor.jsx
// /campaigns/:id — edit a campaign's ticket face, landing page, optional
// pancake attachment, and print batches from it.
//
// Sections top-to-bottom:
//   1. Basics            — name, archive toggle
//   2. Ticket face       — prints on thermal ticket (tagline, expiry, serial)
//   3. Landing page      — headline, body, buttons (up to 4)
//   4. Pancake attachment — optional order picker
//   5. Print             — quantity + batch label + fire print
//   6. History           — past print batches (collapsed)

import { useEffect, useState } from "react";
import { collection, query, where, limit, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import {
  getCampaign, saveCampaign, printCampaignTokens, computeExpiryDisplay,
} from "../../utils/campaigns";

import { STAFF_THEME, FONT } from "../../theme/staff";
const B = STAFF_THEME;

const DRAFT_DEBOUNCE_MS = 600;
const MAX_BUTTONS       = 4;

export default function CampaignEditor({ id, navigate }) {
  const [campaign, setCampaign] = useState(null);
  const [draft, setDraft]       = useState(null);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);

  // Print flow state
  const [printQty, setPrintQty]         = useState(1);
  const [batchLabel, setBatchLabel]     = useState("");
  const [printStatus, setPrintStatus]   = useState({ pct: 0, msg: "" });
  const [printing, setPrinting]         = useState(false);

  // Order picker state
  const [showPicker, setShowPicker]     = useState(false);
  const [pickerOrders, setPickerOrders] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerError, setPickerError]   = useState(null);
  const [pickerSearch, setPickerSearch] = useState("");

  useEffect(() => {
    getCampaign(id).then(c => {
      if (!c) { setError("Campaign not found"); return; }
      setCampaign(c);
      setDraft(c);
    });
  }, [id]);

  // Debounced autosave
  useEffect(() => {
    if (!draft || !campaign) return;
    if (isSameForSave(draft, campaign)) return;
    const t = setTimeout(async () => {
      setSaving(true);
      try {
        const { id, createdAt, updatedAt, totalPrinted, totalScanned, lastScannedAt, printBatches, ...rest } = draft;
        await saveCampaign(id, rest);
        setCampaign(draft);
      } catch (e) { setError(e.message); }
      finally { setSaving(false); }
    }, DRAFT_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [draft, campaign]);

  async function openOrderPicker() {
    // Open the modal immediately so the user sees loading state, then fetch.
    // Query uses a single where-clause (no composite index needed) and we sort
    // client-side — matches the pattern in GalleryView.
    setShowPicker(true);
    if (pickerOrders.length > 0) return;
    setPickerLoading(true);
    setPickerError(null);
    try {
      const snap = await getDocs(query(
        collection(db, "orders"),
        where("state", "==", "done"),
        limit(200),
      ));
      const orders = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(o => !o.hidden && !o.trashedAt)
        .sort((a, b) => {
          const am = a.completedAt?.toMillis?.() ?? a.photographedAt?.toMillis?.() ?? a.createdAt?.toMillis?.() ?? 0;
          const bm = b.completedAt?.toMillis?.() ?? b.photographedAt?.toMillis?.() ?? b.createdAt?.toMillis?.() ?? 0;
          return bm - am;
        });
      setPickerOrders(orders);
    } catch (e) {
      setPickerError(e.message);
    } finally {
      setPickerLoading(false);
    }
  }

  function attachOrder(order) {
    update("linkedOrderId", order.id);
    update("_linkedOrderPreview", {
      subjectName: order.subjectName || order.recipientName || "(unnamed)",
      catalogNumber: order.catalogNumber,
      cutoutUrl: order.cutoutThumbnailUrl || order.photo_cutout_url || null,
    });
    setShowPicker(false);
  }

  function detachOrder() {
    update("linkedOrderId", null);
    update("_linkedOrderPreview", null);
  }

  function update(field, value) {
    setDraft(d => ({ ...d, [field]: value }));
  }

  async function handlePrint() {
    if (!draft || printing) return;
    if (!Number.isFinite(printQty) || printQty < 1) return;
    setPrinting(true);
    setPrintStatus({ pct: 5, msg: "Starting..." });
    try {
      await printCampaignTokens(draft, printQty, {
        batchLabel: batchLabel.trim() || null,
        onStatus: setPrintStatus,
      });
      setTimeout(async () => {
        setPrintStatus({ pct: 0, msg: "" });
        setPrinting(false);
        // Refresh to pick up updated totals
        const c = await getCampaign(id);
        setCampaign(c); setDraft(c);
      }, 1800);
    } catch (e) {
      setPrintStatus({ pct: 0, msg: e.message });
      setPrinting(false);
    }
  }

  if (error) return <ErrorScreen message={error} onBack={() => navigate("/campaigns")} />;
  if (!draft) return <LoadingScreen />;

  const buttonsReachedMax = (draft.buttons || []).length >= MAX_BUTTONS;

  return (
    <div style={{ minHeight: "100vh", background: B.dark, color: "white", fontFamily: FONT.body, paddingBottom: "4rem" }}>

      {/* Top bar — ← All campaigns pinned right. */}
      <div style={{ position: "sticky", top: 0, zIndex: 5, background: B.panel, borderBottom: `1px solid ${B.border}`, padding: "0.6rem 0.9rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <input
            value={draft.name || ""}
            onChange={e => update("name", e.target.value)}
            placeholder="Campaign name"
            style={{ width: "100%", background: "transparent", border: "none", color: B.gold, fontWeight: 800, fontSize: "1rem", outline: "none" }}
          />
        </div>
        <div style={{ color: B.dim, fontSize: "0.72rem", minWidth: "60px", textAlign: "right" }}>
          {saving ? "saving…" : "✓ saved"}
        </div>
        <button onClick={() => navigate("/campaigns")} style={hud()}>← All campaigns</button>
      </div>

      <div style={{ padding: "1rem", display: "flex", flexDirection: "column", gap: "1rem", maxWidth: "560px", margin: "0 auto" }}>

        {/* TICKET FACE */}
        <Section title="Ticket face" subtitle="What prints on the thermal ticket itself.">
          <LabeledInput label="Tagline" value={draft.ticketTagline} onChange={v => update("ticketTagline", v)} placeholder="You got handed a Golden Ticket." />
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <div style={{ flex: 2 }}>
              <LabeledInput label="Expiry text" value={draft.ticketExpiryText} onChange={v => update("ticketExpiryText", v)} placeholder={computeExpiryDisplay(draft) || "Good through April 30"} />
            </div>
            <div style={{ flex: 1 }}>
              <LabeledInput label="Serial prefix" value={draft.serialPrefix} onChange={v => update("serialPrefix", v)} placeholder="GT" />
            </div>
          </div>
          {!draft.ticketExpiryText && computeExpiryDisplay(draft) && (
            <div style={{ color: B.dim, fontSize: "0.7rem" }}>
              ↳ will print “{computeExpiryDisplay(draft)}” (from Expiration setting below)
            </div>
          )}
        </Section>

        {/* EXPIRATION */}
        <Section title="Expiration" subtitle="Controls when scanned tickets stop working and what the printed expiry text defaults to.">
          <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
            {[
              { id: "never",    label: "Never" },
              { id: "duration", label: "After a duration" },
              { id: "absolute", label: "Specific date" },
            ].map(opt => (
              <button
                key={opt.id}
                onClick={() => update("expirationMode", opt.id)}
                style={{
                  background: draft.expirationMode === opt.id ? B.gold : "transparent",
                  color: draft.expirationMode === opt.id ? B.dark : B.muted,
                  border: `1px solid ${draft.expirationMode === opt.id ? B.gold : B.border}`,
                  borderRadius: "8px",
                  padding: "0.4rem 0.75rem",
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {draft.expirationMode === "duration" && (
            <DurationPicker
              minutes={draft.expirationMinutes ?? 1440}
              onChange={v => update("expirationMinutes", v)}
            />
          )}

          {draft.expirationMode === "absolute" && (
            <div>
              <div style={{ color: B.muted, fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.15rem" }}>Expires on</div>
              <input
                type="datetime-local"
                value={toDatetimeLocal(draft.expirationDate)}
                onChange={e => update("expirationDate", e.target.value ? new Date(e.target.value).toISOString() : null)}
                style={{ width: "100%", boxSizing: "border-box", background: B.card, border: `1px solid ${B.border}`, borderRadius: "8px", color: "white", fontSize: "0.88rem", padding: "0.45rem 0.6rem", outline: "none" }}
              />
            </div>
          )}
        </Section>

        {/* LANDING PAGE */}
        <Section title="Landing page" subtitle={draft.linkedOrderId ? "The attached pancake becomes the hero image on the flyer. Your headline, body, and buttons surround it." : "Shown when someone scans the QR. Treat it as a peculiar event flyer, not a keepsake."}>
          <LabeledInput label="Headline" value={draft.headline} onChange={v => update("headline", v)} placeholder="Free Pancake Art Saturday" />
          <LabeledTextarea label="Body" value={draft.body} onChange={v => update("body", v)} placeholder="Stop by Tower Grove 9–1 this Saturday. Bring a friend." rows={3} />

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "0.3rem" }}>
            <div style={{ color: B.cream, fontSize: "0.78rem", fontWeight: 700 }}>Buttons ({(draft.buttons || []).length}/{MAX_BUTTONS})</div>
            <button
              onClick={() => update("buttons", [...(draft.buttons || []), { label: "", url: "" }])}
              disabled={buttonsReachedMax}
              style={{ ...secondaryBtn(), opacity: buttonsReachedMax ? 0.4 : 1 }}
            >
              + Add button
            </button>
          </div>
          {(draft.buttons || []).map((btn, i) => (
            // Two-row layout: label + remove on row 1, URL full-width on row 2.
            // Keeps the remove button reachable without horizontal scrolling on
            // narrow screens.
            <div key={i} style={{ display: "flex", flexDirection: "column", gap: "0.3rem", background: B.card, border: `1px solid ${B.border}`, borderRadius: "8px", padding: "0.4rem" }}>
              <div style={{ display: "flex", gap: "0.3rem", alignItems: "center" }}>
                <input
                  value={btn.label}
                  onChange={e => update("buttons", draft.buttons.map((b, j) => j === i ? { ...b, label: e.target.value } : b))}
                  placeholder="Button label"
                  style={{ flex: 1, minWidth: 0, background: B.raised, border: `1px solid ${B.border}`, borderRadius: "6px", color: "white", fontSize: "0.78rem", padding: "0.35rem 0.5rem", outline: "none" }}
                />
                <button
                  onClick={() => update("buttons", draft.buttons.filter((_, j) => j !== i))}
                  aria-label="Remove button"
                  style={{ ...secondaryBtn(), padding: "0.35rem 0.6rem", flexShrink: 0, borderColor: "#c0392b", color: "#e74c3c" }}
                >
                  Remove
                </button>
              </div>
              <input
                value={btn.url}
                onChange={e => update("buttons", draft.buttons.map((b, j) => j === i ? { ...b, url: e.target.value } : b))}
                placeholder="https://..."
                style={{ width: "100%", boxSizing: "border-box", background: B.raised, border: `1px solid ${B.border}`, borderRadius: "6px", color: "white", fontSize: "0.78rem", padding: "0.35rem 0.5rem", outline: "none" }}
              />
            </div>
          ))}
        </Section>

        {/* PANCAKE ATTACHMENT */}
        <Section title="Pancake attachment (optional)" subtitle="Attach a pancake from the gallery to use as the flyer's hero image. A memorable visual hook — not a gift, not a keepsake. Leave blank for a text-only flyer.">
          {draft.linkedOrderId ? (
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", background: B.card, border: `1px solid ${B.border}`, borderRadius: "10px", padding: "0.5rem" }}>
              {draft._linkedOrderPreview?.cutoutUrl && <img src={draft._linkedOrderPreview.cutoutUrl} alt="" style={{ width: "44px", height: "44px", borderRadius: "6px", objectFit: "contain" }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: "white", fontWeight: 700, fontSize: "0.85rem" }}>{draft._linkedOrderPreview?.subjectName || "Pancake attached"}</div>
                {draft._linkedOrderPreview?.catalogNumber && <div style={{ color: B.muted, fontSize: "0.72rem" }}>#{draft._linkedOrderPreview.catalogNumber}</div>}
              </div>
              <button onClick={detachOrder} style={secondaryBtn()}>Detach</button>
            </div>
          ) : (
            <button onClick={openOrderPicker} style={{ ...secondaryBtn(), padding: "0.55rem 0.9rem" }}>📎 Attach a pancake</button>
          )}
        </Section>

        {/* PRINT */}
        <Section title="Print tickets" subtitle="Each print creates a batch of unique QR codes. All share this campaign's settings.">
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <div>
              <div style={{ color: B.muted, fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.15rem" }}>Quantity</div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                <button onClick={() => setPrintQty(q => Math.max(1, (Number.isFinite(q) ? q : 1) - 1))} style={qtyBtn()}>−</button>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  value={printQty}
                  onChange={e => {
                    const n = parseInt(e.target.value, 10);
                    setPrintQty(Number.isFinite(n) && n > 0 ? n : "");
                  }}
                  onBlur={() => { if (!Number.isFinite(printQty) || printQty < 1) setPrintQty(1); }}
                  style={{ flex: 1, minWidth: 0, background: B.card, border: `1px solid ${B.border}`, borderRadius: "8px", color: "white", fontSize: "1.2rem", fontWeight: 800, padding: "0.45rem 0.6rem", textAlign: "center", outline: "none" }}
                />
                <button onClick={() => setPrintQty(q => (Number.isFinite(q) ? q : 0) + 1)} style={qtyBtn()}>+</button>
              </div>
            </div>
            <LabeledInput label="Batch label (optional)" value={batchLabel} onChange={setBatchLabel} placeholder="West County coffee shops" />
          </div>

          {printStatus.msg && (
            <div style={{ marginTop: "0.5rem" }}>
              <div style={{ background: B.card, borderRadius: "999px", height: "6px", overflow: "hidden" }}>
                <div style={{ background: B.gold, height: "100%", width: `${printStatus.pct}%`, transition: "width 0.3s" }} />
              </div>
              <div style={{ color: B.muted, fontSize: "0.72rem", marginTop: "0.2rem" }}>{printStatus.msg}</div>
            </div>
          )}

          <button onClick={handlePrint} disabled={printing} style={{ ...primaryBtn(), opacity: printing ? 0.6 : 1, marginTop: "0.5rem" }}>
            {printing ? "Printing..." : `🖨 Print ${printQty} ticket${printQty > 1 ? "s" : ""}`}
          </button>
        </Section>

        {/* HISTORY */}
        {(campaign.printBatches || []).length > 0 && (
          <Section title={`Print history (${campaign.printBatches.length})`}>
            {campaign.printBatches.slice().reverse().map((b, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: B.card, border: `1px solid ${B.border}`, borderRadius: "8px", padding: "0.45rem 0.65rem", fontSize: "0.78rem" }}>
                <div>
                  <div style={{ color: B.cream }}>{b.batchLabel || "(no label)"}</div>
                  <div style={{ color: B.dim, fontSize: "0.7rem" }}>{new Date(b.printedAt).toLocaleString()}</div>
                </div>
                <div style={{ color: B.gold, fontWeight: 700 }}>×{b.qty}</div>
              </div>
            ))}
          </Section>
        )}

        {/* Stats */}
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <Stat label="Printed"  value={campaign.totalPrinted || 0} />
          <Stat label="Scanned"  value={campaign.totalScanned || 0} />
        </div>
      </div>

      {/* Order picker modal */}
      {showPicker && (
        <OrderPickerModal
          orders={pickerOrders}
          loading={pickerLoading}
          error={pickerError}
          search={pickerSearch}
          onSearchChange={setPickerSearch}
          onPick={attachOrder}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}

// Number + unit picker for duration-mode expiration. Converts to/from minutes.
function DurationPicker({ minutes, onChange }) {
  // Pick the largest sensible unit for display so "1440 min" shows as "1 day".
  const derived = deriveDurationUnit(minutes);
  const [value, setValue] = useState(derived.value);
  const [unit, setUnit]   = useState(derived.unit);

  // When parent value changes externally (e.g. loading a campaign), resync.
  useEffect(() => {
    const d = deriveDurationUnit(minutes);
    setValue(d.value); setUnit(d.unit);
  }, [minutes]);

  function push(v, u) {
    const mult = { minutes: 1, hours: 60, days: 1440, weeks: 10080, months: 43200 }[u] || 1;
    onChange(Math.max(1, Math.round(v * mult)));
  }

  return (
    <div>
      <div style={{ color: B.muted, fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.15rem" }}>
        Expires after printing
      </div>
      <div style={{ display: "flex", gap: "0.4rem" }}>
        <input
          type="number"
          min={1}
          value={value}
          onChange={e => {
            const n = parseFloat(e.target.value);
            setValue(e.target.value);
            if (Number.isFinite(n) && n > 0) push(n, unit);
          }}
          style={{ flex: 1, minWidth: 0, background: B.card, border: `1px solid ${B.border}`, borderRadius: "8px", color: "white", fontSize: "0.9rem", padding: "0.45rem 0.6rem", outline: "none" }}
        />
        <select
          value={unit}
          onChange={e => { setUnit(e.target.value); push(parseFloat(value) || 1, e.target.value); }}
          style={{ background: B.card, border: `1px solid ${B.border}`, borderRadius: "8px", color: "white", fontSize: "0.9rem", padding: "0.45rem 0.6rem", outline: "none" }}
        >
          <option value="minutes">minutes</option>
          <option value="hours">hours</option>
          <option value="days">days</option>
          <option value="weeks">weeks</option>
          <option value="months">months</option>
        </select>
      </div>
    </div>
  );
}

function deriveDurationUnit(minutes) {
  const m = Number.isFinite(minutes) ? minutes : 1440;
  if (m % 43200 === 0 && m >= 43200) return { value: m / 43200, unit: "months" };
  if (m % 10080 === 0 && m >= 10080) return { value: m / 10080, unit: "weeks" };
  if (m % 1440  === 0 && m >= 1440)  return { value: m / 1440,  unit: "days" };
  if (m % 60    === 0 && m >= 60)    return { value: m / 60,    unit: "hours" };
  return { value: m, unit: "minutes" };
}

function toDatetimeLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  // datetime-local wants YYYY-MM-DDTHH:MM in local time.
  const pad = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ── SHARED UI ─────────────────────────────────────────────────────────────

function Section({ title, subtitle, children }) {
  return (
    <div style={{ background: B.panel, border: `1px solid ${B.divider}`, borderRadius: "12px", padding: "0.85rem", display: "flex", flexDirection: "column", gap: "0.55rem" }}>
      <div>
        <div style={{ color: B.cream, fontWeight: 800, fontSize: "0.88rem" }}>{title}</div>
        {subtitle && <div style={{ color: B.dim, fontSize: "0.72rem", marginTop: "0.15rem" }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function LabeledInput({ label, value, onChange, placeholder }) {
  return (
    <div>
      <div style={{ color: B.muted, fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.15rem" }}>{label}</div>
      <input
        value={value ?? ""}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ width: "100%", boxSizing: "border-box", background: B.card, border: `1px solid ${B.border}`, borderRadius: "8px", color: "white", fontSize: "0.88rem", padding: "0.45rem 0.6rem", outline: "none" }}
      />
    </div>
  );
}

function LabeledTextarea({ label, value, onChange, placeholder, rows = 3 }) {
  return (
    <div>
      <div style={{ color: B.muted, fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.15rem" }}>{label}</div>
      <textarea
        value={value ?? ""}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        style={{ width: "100%", boxSizing: "border-box", background: B.card, border: `1px solid ${B.border}`, borderRadius: "8px", color: "white", fontSize: "0.88rem", padding: "0.45rem 0.6rem", outline: "none", resize: "vertical", fontFamily: "inherit" }}
      />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ flex: 1, background: B.panel, border: `1px solid ${B.divider}`, borderRadius: "10px", padding: "0.6rem", textAlign: "center" }}>
      <div style={{ color: B.gold, fontSize: "1.4rem", fontWeight: 900 }}>{value}</div>
      <div style={{ color: B.muted, fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</div>
    </div>
  );
}

function OrderPickerModal({ orders, loading, error, search, onSearchChange, onPick, onClose }) {
  const filtered = (search.trim()
    ? orders.filter(o => {
        const s = search.toLowerCase();
        return (o.subjectName || "").toLowerCase().includes(s)
            || (o.recipientName || "").toLowerCase().includes(s)
            || (o.catalogNumber && `#${o.catalogNumber}`.includes(s));
      })
    : orders).slice(0, 40);
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem", zIndex: 100 }}>
      <div style={{ background: B.panel, borderRadius: "14px", padding: "1rem", maxWidth: "420px", width: "100%", maxHeight: "85vh", display: "flex", flexDirection: "column", border: `1px solid ${B.gold}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.6rem" }}>
          <div style={{ color: B.gold, fontWeight: 800 }}>Attach a pancake</div>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{ background: "none", border: "none", color: B.muted, fontSize: "1.2rem", cursor: "pointer" }}>✕</button>
        </div>
        <input value={search} onChange={e => onSearchChange(e.target.value)} placeholder="Search by name or #number…"
          style={{ background: B.card, border: `1px solid ${B.border}`, borderRadius: "8px", color: "white", fontSize: "0.85rem", padding: "0.45rem 0.7rem", marginBottom: "0.6rem", outline: "none" }} />
        <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: "0.3rem" }}>
          {loading && <div style={{ color: B.muted, fontSize: "0.82rem", textAlign: "center", padding: "1rem" }}>Loading pancakes…</div>}
          {error && <div style={{ color: "#e74c3c", fontSize: "0.82rem", textAlign: "center", padding: "1rem" }}>Error: {error}</div>}
          {!loading && !error && filtered.map(o => {
            const src = o.cutoutThumbnailUrl || o.photo_cutout_url;
            return (
              <div key={o.id} onClick={() => onPick(o)} style={{ display: "flex", alignItems: "center", gap: "0.5rem", background: B.card, border: `1px solid ${B.border}`, borderRadius: "8px", padding: "0.4rem", cursor: "pointer" }}>
                {src && <img src={src} alt="" style={{ width: "36px", height: "36px", objectFit: "contain", borderRadius: "4px" }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: "white", fontSize: "0.82rem", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {o.subjectName || o.recipientName || "(unnamed)"}
                  </div>
                  <div style={{ color: B.dim, fontSize: "0.68rem" }}>
                    {o.catalogNumber ? `#${o.catalogNumber}` : ""}{o.artistName ? ` · ${o.artistName}` : ""}
                  </div>
                </div>
              </div>
            );
          })}
          {!loading && !error && filtered.length === 0 && <div style={{ color: B.muted, fontSize: "0.82rem", textAlign: "center", padding: "1rem" }}>No matches.</div>}
        </div>
      </div>
    </div>
  );
}

function LoadingScreen() {
  return <div style={{ minHeight: "100vh", background: B.dark, color: B.muted, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT.body }}>Loading…</div>;
}
function ErrorScreen({ message, onBack }) {
  return (
    <div style={{ minHeight: "100vh", background: B.dark, color: "white", fontFamily: FONT.body, padding: "2rem", textAlign: "center" }}>
      <div style={{ fontSize: "1.5rem", color: B.pink, marginBottom: "0.5rem" }}>⚠️ {message}</div>
      <button onClick={onBack} style={secondaryBtn()}>← Back to campaigns</button>
    </div>
  );
}

// Exclude cosmetic/rollup fields from diff comparison so autosave doesn't
// churn on every keystroke.
function isSameForSave(a, b) {
  const keys = ["name", "archived", "ticketTagline", "ticketExpiryText", "serialPrefix", "headline", "body", "heroImageUrl", "buttons", "linkedOrderId", "expirationMode", "expirationMinutes", "expirationDate", "delivery", "recipients", "landingMode"];
  for (const k of keys) {
    if (JSON.stringify(a?.[k] ?? null) !== JSON.stringify(b?.[k] ?? null)) return false;
  }
  return true;
}

function hud() {
  return { background: "transparent", border: `1px solid ${B.border}`, color: B.muted, borderRadius: "8px", padding: "0.3rem 0.65rem", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" };
}
function primaryBtn() {
  return { width: "100%", background: B.gold, color: B.dark, border: "none", borderRadius: "10px", padding: "0.65rem", fontSize: "0.92rem", fontWeight: 800, cursor: "pointer" };
}
function secondaryBtn() {
  return { background: "transparent", border: `1px solid ${B.border}`, color: B.muted, borderRadius: "8px", padding: "0.3rem 0.6rem", fontSize: "0.74rem", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" };
}
function qtyBtn() {
  return { width: "36px", height: "36px", borderRadius: "50%", background: B.raised, border: `1px solid ${B.border}`, color: "white", fontSize: "1.2rem", fontWeight: 700, cursor: "pointer" };
}
